/**
 * API para Registro de Calificaciones
 * 
 * Maneja todas las operaciones relacionadas con el registro de calificaciones
 * por grupo y evaluación.
 */

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';

/**
 * Avisa a otras pantallas (p. ej. Asignación de Grupos) que las calificaciones
 * de los estudiantes indicados cambiaron, para refrescar sus créditos.
 */
export function notifyCalificacionesUpdated(idsEstudiante = []) {
  try {
    window.dispatchEvent(new CustomEvent('calificaciones:updated', {
      detail: { ids_estudiante: idsEstudiante.filter(Boolean) },
    }));
  } catch (_) {}
}

/**
 * Obtiene el token de autenticación del localStorage
 */
function getAuthToken() {
  return localStorage.getItem('authToken');
}

/**
 * Headers comunes para las peticiones
 * Incluye cache-busting para evitar respuestas cacheadas por el navegador
 */
function getHeaders() {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
}

/**
 * Obtiene la información del asesor basado en el usuario autenticado
 * @returns {Promise<Object>} Información del usuario, asesor e institución
 */
export async function getInfoAsesorUsuario() {
  try {
    const _t = Date.now();
    const res = await fetch(`${API_BASE}/escolar/calificaciones/info-asesor/?_t=${_t}`, {
      headers: getHeaders(),
      cache: 'no-store'
    });
    
    if (!res.ok) {
      console.error('[calificaciones.js] Error al obtener info asesor:', res.status);
      throw new Error('Error al obtener información del asesor');
    }
    
    return await res.json();
  } catch (error) {
    console.error('[calificaciones.js] Error:', error);
    throw error;
  }
}

/**
 * Obtiene los grupos asignados a un asesor
 * @param {Object} params - Parámetros de búsqueda
 * @param {number} params.id_asesor - ID del asesor
 * @param {number} [params.id_campus] - ID del campus (opcional)
 * @param {string} [params.search] - Texto de búsqueda (opcional)
 * @returns {Promise<Object>} Lista de grupos
 */
export async function getGruposAsesor({ id_asesor, id_campus, search }) {
  // Validar que id_asesor sea un número válido y mayor que 0
  if (!id_asesor || isNaN(Number(id_asesor)) || Number(id_asesor) <= 0) {
    console.warn('[calificaciones.js] id_asesor inválido:', id_asesor);
    return { grupos: [], total: 0, debug: { error: 'id_asesor inválido en frontend' } };
  }
  try {
    const params = new URLSearchParams();
    params.append('id_asesor', id_asesor);
    if (id_campus) params.append('id_campus', id_campus);
    if (search) params.append('search', search);
    params.append('_t', Date.now());
    
    const res = await fetch(`${API_BASE}/escolar/calificaciones/grupos-asesor/?${params.toString()}`, {
      headers: getHeaders(),
      cache: 'no-store'
    });
    
    if (!res.ok) {
      console.error('[calificaciones.js] Error al obtener grupos:', res.status);
      throw new Error('Error al obtener grupos del asesor');
    }
    
    return await res.json();
  } catch (error) {
    console.error('[calificaciones.js] Error:', error);
    throw error;
  }
}

/**
 * Obtiene los grupos del asesor autenticado (el id_asesor se deriva del token JWT).
 * Usar en pantallas de asesor (Rúbrica/Firma Concentrados) en lugar de getGruposAsesor.
 */
export async function getGruposAsesorMe() {
  try {
    const res = await fetch(
      `${API_BASE}/escolar/calificaciones/grupos-asesor/me/?_t=${Date.now()}`,
      {
        headers: getHeaders(),
        cache: 'no-store',
      }
    );
    if (!res.ok) {
      console.error('[calificaciones.js] getGruposAsesorMe error:', res.status);
      throw new Error('Error al obtener grupos del asesor');
    }
    return await res.json();
  } catch (error) {
    console.error('[calificaciones.js] getGruposAsesorMe:', error);
    throw error;
  }
}

/**
 * Obtiene los estudiantes de un grupo con sus calificaciones
 * @param {number} grupoId - ID del grupo
 * @param {string} tipoEvaluacion - Tipo de evaluación (PARCIAL1, PARCIAL2, etc.)
 * @returns {Promise<Object>} Lista de estudiantes con calificaciones
 */
export async function getEstudiantesGrupo(grupoId, tipoEvaluacion = 'PARCIAL1') {
  try {
    const params = new URLSearchParams();
    if (tipoEvaluacion) params.append('tipo_evaluacion', tipoEvaluacion);
    const url = `${API_BASE}/escolar/calificaciones/estudiantes/${grupoId}/?${params.toString()}`;
    console.log('[calificaciones.js] Llamando a getEstudiantesGrupo:', url);
    const res = await fetch(url, { headers: getHeaders() });
    console.log('[calificaciones.js] Response status:', res.status);
    if (!res.ok) {
      console.error('[calificaciones.js] Error al obtener estudiantes:', res.status);
      throw new Error('Error al obtener estudiantes del grupo');
    }
    return await res.json();
  } catch (error) {
    console.error('[calificaciones.js] Error:', error);
    throw error;
  }
}

/**
 * Guarda las calificaciones de los estudiantes
 * @param {Object} data - Datos de calificaciones
 * @param {number} data.id_grupo - ID del grupo
 * @param {string} data.tipo_evaluacion - Tipo de evaluación
 * @param {Array} data.calificaciones - Array de calificaciones
 * @returns {Promise<Object>} Resultado del guardado
 */
export async function guardarCalificaciones(data) {
  try {
    const res = await fetch(`${API_BASE}/escolar/calificaciones/guardar/`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data)
    });
    
    if (!res.ok) {
      const errorData = await res.json();
      console.error('[calificaciones.js] Error al guardar:', res.status, errorData);
      throw new Error(errorData.error || 'Error al guardar calificaciones');
    }
    
    return await res.json();
  } catch (error) {
    console.error('[calificaciones.js] Error:', error);
    throw error;
  }
}

/**
 * Obtiene los tipos de evaluación disponibles
 * @returns {Promise<Object>} Lista de tipos de evaluación
 */
export async function getTiposEvaluacion() {
  try {
    const res = await fetch(`${API_BASE}/escolar/calificaciones/tipos-evaluacion/`, {
      headers: getHeaders()
    });
    
    if (!res.ok) {
      console.error('[calificaciones.js] Error al obtener tipos evaluación:', res.status);
      throw new Error('Error al obtener tipos de evaluación');
    }
    
    return await res.json();
  } catch (error) {
    console.error('[calificaciones.js] Error:', error);
    throw error;
  }
}

/**
 * Obtiene la boleta de calificaciones de un estudiante.
 * Devuelve encabezado + ciclos con materias y calificaciones por parcial/final/extraordinario.
 * @param {number} idEstudiante - ID del estudiante
 * @param {object} params - { id_ciclo_escolar } opcional
 * @returns {Promise<Object>} { encabezado, ciclos }
 */
export async function getBoletaEstudiante(idEstudiante, params = {}) {
  try {
    const qs = new URLSearchParams();
    if (params.id_ciclo_escolar) qs.append('id_ciclo_escolar', params.id_ciclo_escolar);
    const query = qs.toString() ? `?${qs.toString()}` : '';

    const res = await fetch(`${API_BASE}/escolar/calificaciones/boleta/${idEstudiante}/${query}`, {
      headers: getHeaders()
    });

    if (!res.ok) {
      console.error('[calificaciones.js] Error al obtener boleta:', res.status);
      throw new Error('Error al obtener boleta de calificaciones');
    }

    return await res.json();
  } catch (error) {
    console.error('[calificaciones.js] Error getBoletaEstudiante:', error);
    throw error;
  }
}

/**
 * Obtiene la boleta del estudiante autenticado.
 * @param {object} params - { id_ciclo_escolar } opcional
 * @returns {Promise<Object>} { encabezado, ciclos }
 */
export async function getMiBoletaEstudiante(params = {}) {
  try {
    const qs = new URLSearchParams();
    if (params.id_ciclo_escolar) qs.append('id_ciclo_escolar', params.id_ciclo_escolar);
    const query = qs.toString() ? `?${qs.toString()}` : '';

    const res = await fetch(`${API_BASE}/escolar/calificaciones/boleta/me/${query}`, {
      headers: getHeaders(),
      cache: 'no-store'
    });

    if (!res.ok) {
      console.error('[calificaciones.js] Error al obtener mi boleta:', res.status);
      throw new Error('Error al obtener boleta de calificaciones del estudiante autenticado');
    }

    return await res.json();
  } catch (error) {
    console.error('[calificaciones.js] Error getMiBoletaEstudiante:', error);
    throw error;
  }
}

/**
 * Obtiene la lista de campus disponibles
 * @returns {Promise<Array>} Lista de campus
 */
export async function getCampusDisponibles() {
  try {
    const res = await fetch(`${API_BASE}/catalogos/campus/select/`, {
      headers: getHeaders()
    });
    
    if (!res.ok) {
      console.error('[calificaciones.js] Error al obtener campus:', res.status);
      throw new Error('Error al obtener campus');
    }
    
    const data = await res.json();
    return data.map(c => ({
      id: c.id_campus,
      nombre: c.nombre_campus,
      codigo: c.codigo_campus
    }));
  } catch (error) {
    console.error('[calificaciones.js] Error:', error);
    throw error;
  }
}

/**
 * Crea o actualiza un registro de esc_rubrica.
 * @param {Object} data - Datos del parcial del estudiante
 * @returns {Promise<Object>} Registro guardado
 */
export async function guardarRubrica(data) {
  try {
    const res = await fetch(`${API_BASE}/escolar/rubrica/guardar/`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data)
    });

    if (!res.ok) {
      const errorData = await res.json();
      console.error('[calificaciones.js] Error al guardar rúbrica:', res.status, errorData);
      throw new Error(errorData.error || 'Error al guardar rúbrica');
    }

    return await res.json();
  } catch (error) {
    console.error('[calificaciones.js] Error guardarRubrica:', error);
    throw error;
  }
}

/**
 * Obtiene el reporte de captura de calificaciones.
 *
 * @param {Object} params
 * @param {number|string} params.id_ciclo_escolar - REQUERIDO.
 * @param {number|string} [params.id_campus]
 * @param {number|string} [params.id_seccion]
 * @param {string} [params.parcial] - PARCIAL1 | PARCIAL2 | PARCIAL3
 * @returns {Promise<{parcial:string,filtros:object,total_grupos:number,resultados:Array}>}
 */
export async function getReporteCapturaCalificaciones({
  id_ciclo_escolar,
  id_campus,
  id_seccion,
  parcial,
} = {}) {
  if (!id_ciclo_escolar) {
    throw new Error("Parámetro 'id_ciclo_escolar' es requerido.");
  }

  const qs = new URLSearchParams();
  qs.set('id_ciclo_escolar', String(id_ciclo_escolar));
  if (id_campus !== undefined && id_campus !== null && id_campus !== '' && id_campus !== 'todos') {
    qs.set('id_campus', String(id_campus));
  }
  if (id_seccion !== undefined && id_seccion !== null && id_seccion !== '' && id_seccion !== 'todos') {
    qs.set('id_seccion', String(id_seccion));
  }
  if (parcial) {
    qs.set('parcial', String(parcial));
  }

  const url = `${API_BASE}/escolar/calificaciones/reporte-captura/?${qs.toString()}`;
  const res = await fetch(url, { headers: getHeaders() });

  if (!res.ok) {
    let errorMsg = `Error ${res.status} al obtener el reporte`;
    try {
      const errorData = await res.json();
      errorMsg = errorData.error || errorMsg;
    } catch (_) { /* ignore parse error */ }
    throw new Error(errorMsg);
  }

  return await res.json();
}

